// Ficha tecnica no Mercado Livre (plano mapeamento-campos F5/F7): cada campo do anuncio mostra o valor que vai
// e DE ONDE vem (Sisplan, categoria, padrao do canal, cadastro do produto), com "Sobrescrever" so neste canal.
// Substitui o card "Campos do canal" no canal ML. Grava em field_overrides do canal; o que a tela nao conhece
// nesse JSON e preservado.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { api, apiErrorMessage } from "../../lib/api";
import type { AttrChoice, MlFicha, MlFichaField, ProductAttribute } from "../../lib/ml-ficha";
import { categoryValueLabel } from "../../lib/ml-ficha";
import { AiGenerateButton, AiSuggestAttributesButton } from "../AiGenerateButton";
import { LISTING_TYPES } from "../../lib/ml-channel";
import { fichaPatch, freeShippingText, originTone, titleCounter } from "../../lib/ml-ficha";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select } from "../ui/select";
import { Switch } from "../ui/switch";

const listingLabel = (v: string): string => LISTING_TYPES.find((l) => l.value === v)?.label ?? v;

export function MlFichaCard({
  slug,
  sku,
  ficha,
  fieldOverrides,
  priceText,
  disabled,
}: {
  slug: string;
  sku: string;
  ficha: MlFicha;
  fieldOverrides: Record<string, unknown>;
  /** Preco normal do Sisplan (texto da API) para a previa do frete gratis. */
  priceText: string | null;
  disabled: boolean;
}): JSX.Element {
  const qc = useQueryClient();
  // draft: chave -> valor do override ("" = sem override). Comeca do que ja esta gravado.
  const initial = useMemo(() => {
    const d: Record<string, string> = {};
    for (const f of ficha.fields) d[f.key] = f.override ?? "";
    d.ml_composition = ficha.composition.override ?? "";
    return d;
  }, [ficha]);
  const [draft, setDraft] = useState<Record<string, string>>(initial);
  const [on, setOn] = useState<Record<string, boolean>>(() => Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v !== ""])));
  useEffect(() => {
    setDraft(initial);
    setOn(Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v !== ""])));
  }, [initial]);

  // Atributos da categoria do ML (lista oficial do ML) e o que ja foi escolhido neste produto.
  const attrsQ = useQuery<{ category_id: string | null; attributes: ProductAttribute[] }>({
    queryKey: ["ml-product-attrs", slug, sku],
    queryFn: async () => (await api.get<{ category_id: string | null; attributes: ProductAttribute[] }>(`/api/channels/${slug}/products/${sku}/ml/attributes`)).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  const savedAttrs = useMemo<Record<string, AttrChoice>>(() => {
    const raw = fieldOverrides.ml_attrs;
    return raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, AttrChoice>) : {};
  }, [fieldOverrides]);
  const [attrs, setAttrs] = useState<Record<string, AttrChoice>>(savedAttrs);
  useEffect(() => setAttrs(savedAttrs), [savedAttrs]);

  const patch = useMemo(() => fichaPatch(fieldOverrides, draft, on, attrs), [fieldOverrides, draft, on, attrs]);
  const dirty = JSON.stringify(patch) !== JSON.stringify(fichaPatch(fieldOverrides, initial, Object.fromEntries(Object.entries(initial).map(([k, v]) => [k, v !== ""])), savedAttrs));

  const save = useMutation({
    mutationFn: async () => {
      await api.patch(`/api/channels/${slug}/products/${sku}`, { field_overrides: patch });
    },
    onSuccess: () => {
      toast.success("Ficha do Mercado Livre salva");
      void qc.invalidateQueries({ queryKey: ["product", slug, sku] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const counter = titleCounter(ficha.title.length, ficha.title.max);
  const priceNum = priceText != null ? Number(priceText) : NaN;

  return (
    <section className="card stack" style={{ gap: ".75rem" }} aria-label="Ficha técnica no Mercado Livre">
      <div className="stack" style={{ gap: ".25rem" }}>
        <h3 className="row__title" style={{ margin: 0 }}>Ficha técnica no Mercado Livre</h3>
        <p className="field__hint" style={{ margin: 0 }}>
          Cada campo mostra o valor que vai no anúncio e de onde ele vem. Para mudar só neste produto, ligue "Sobrescrever". As regras gerais ficam na aba "Regras do Mercado Livre" do canal.
        </p>
      </div>

      <div className="override" role="group" aria-label="Prévia do anúncio">
        <div className="cluster" style={{ alignItems: "baseline" }}>
          <span style={{ fontWeight: 800 }}>Título que vai no anúncio</span>
          <span className="chip" style={counter.over ? { background: "var(--color-bad)", color: "white" } : undefined}>{counter.text}</span>
        </div>
        <p style={{ margin: ".25rem 0 0", fontWeight: 700 }}>{ficha.title.value || "(sem nome)"}</p>
        <p className="field__hint" style={{ margin: ".25rem 0 0" }}>
          {ficha.title.source === "override" ? "Escrito neste canal, no campo Nome." : ficha.title.source === "ml_title_legado" ? "Vem do campo antigo de título do canal." : ficha.title.source === "teste" ? "Canal em modo de teste: título fixo." : "Montado a partir do nome principal do produto."}
          {ficha.title.piece_type ? ` Tipo da peça: ${ficha.title.piece_type}.` : ""}
        </p>
        <p className="field__hint" style={{ margin: ".25rem 0 0" }}>
          Marca: <b style={{ color: "var(--color-ink)" }}>{ficha.brand.value}</b> ({ficha.brand.origin_label}).
          {ficha.category ? ` Categoria no ML: ${ficha.category.ml_category_id} (${ficha.category.name}).` : " Sem categoria do ML para este produto: mapeie em Regras do Mercado Livre."}
          {Number.isFinite(priceNum) ? ` ${freeShippingText(priceNum, ficha.free_shipping_from)}` : ""}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {ficha.fields.map((f) => (
          <FieldRow
            key={f.key}
            f={f}
            active={on[f.key] === true}
            value={draft[f.key] ?? ""}
            disabled={disabled}
            onToggle={(v) => setOn((s) => ({ ...s, [f.key]: v }))}
            onChange={(v) => setDraft((s) => ({ ...s, [f.key]: v }))}
            aiButton={
              f.key === "ml_model" && !disabled ? (
                <AiGenerateButton
                  slug={slug}
                  sku={sku}
                  target="model"
                  label="Sugerir modelo"
                  onResult={(t) => {
                    setDraft((s) => ({ ...s, ml_model: t }));
                    setOn((s) => ({ ...s, ml_model: true }));
                  }}
                />
              ) : null
            }
          />
        ))}
      </div>

      {attrsQ.data && attrsQ.data.attributes.length > 0 ? (
        <fieldset className="stack" style={{ gap: ".5rem", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".625rem", margin: 0, minWidth: 0 }}>
          <legend style={{ fontWeight: 800, padding: "0 .375rem" }}>Atributos da categoria no Mercado Livre</legend>
          <div className="cluster" style={{ justifyContent: "space-between" }}>
            <p className="field__hint" style={{ margin: 0, flex: 1 }}>
              Quanto mais atributos preenchidos, melhor o anúncio aparece. Vazio usa o valor da categoria (se houver). A IA só escolhe valores que existem na lista do Mercado Livre.
            </p>
            {!disabled ? (
              <AiSuggestAttributesButton
                slug={slug}
                sku={sku}
                onResult={(list) =>
                  setAttrs((cur) => ({ ...cur, ...Object.fromEntries(list.map((s) => [s.id, { value_id: s.value_id } as AttrChoice])) }))
                }
              />
            ) : null}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {attrsQ.data.attributes.map((a) => {
              const cur = attrs[a.id];
              const value = cur?.value_id ?? cur?.value_name ?? "";
              const fromCat = categoryValueLabel(a);
              return (
                <div className="field" key={a.id}>
                  <Label htmlFor={`mla-${a.id}`}>
                    {a.name} {a.required ? <span className="chip">obrigatório</span> : null}
                  </Label>
                  {a.values.length > 0 ? (
                    <Select
                      id={`mla-${a.id}`}
                      value={value}
                      disabled={disabled}
                      onChange={(e) => setAttrs((s) => ({ ...s, [a.id]: e.target.value ? { value_id: e.target.value } : {} }))}
                    >
                      <option value="">{fromCat ? `(da categoria: ${fromCat})` : "(sem valor)"}</option>
                      {a.values.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </Select>
                  ) : (
                    <Input
                      id={`mla-${a.id}`}
                      value={value}
                      disabled={disabled}
                      placeholder={fromCat || "(sem valor)"}
                      onChange={(e) => setAttrs((s) => ({ ...s, [a.id]: e.target.value ? { value_name: e.target.value } : {} }))}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>
      ) : attrsQ.isError ? (
        <p className="field__hint" style={{ color: "var(--color-warn)" }}>Não deu para ler os atributos da categoria no Mercado Livre agora ({apiErrorMessage(attrsQ.error)}).</p>
      ) : null}

      <div className={on.ml_composition ? "override override--on" : "override"}>
        <div className="cluster" style={{ marginBottom: ".5rem" }}>
          <Label htmlFor="mlf-comp">Composição</Label>
          <span className="chip">{on.ml_composition ? "escrito neste canal" : ficha.composition.base ? "do cadastro do produto" : "sem valor"}</span>
          {!disabled ? (
            <Switch
              checked={on.ml_composition === true}
              onChange={(e) => setOn((s) => ({ ...s, ml_composition: e.target.checked }))}
              label="Sobrescrever"
              className="ml-auto"
            />
          ) : null}
        </div>
        {on.ml_composition ? (
          <Input id="mlf-comp" value={draft.ml_composition ?? ""} onChange={(e) => setDraft((s) => ({ ...s, ml_composition: e.target.value }))} disabled={disabled} placeholder="94% viscose, 6% elastano" />
        ) : (
          <p style={{ margin: 0 }}>{ficha.composition.base || "Sem composição cadastrada. Preencha no card Composição da base do produto."}</p>
        )}
        <p className="field__hint" style={{ marginTop: ".375rem" }}>
          Dado de etiqueta: o sistema usa a fibra de maior percentual como material principal. Não é gerado por IA.
        </p>
      </div>

      {!disabled ? (
        <div className="cluster" style={{ justifyContent: "flex-end" }}>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!dirty}>Salvar ficha do Mercado Livre</Button>
        </div>
      ) : null}
    </section>
  );
}

function FieldRow({
  f,
  active,
  value,
  disabled,
  onToggle,
  onChange,
  aiButton,
}: {
  f: MlFichaField;
  active: boolean;
  value: string;
  disabled: boolean;
  onToggle: (v: boolean) => void;
  onChange: (v: string) => void;
  aiButton?: ReactNode;
}): JSX.Element {
  const shown = f.key === "ml_listing_type" ? listingLabel(f.value) : f.value;
  const tone = originTone(f.origin);
  return (
    <div className={active ? "override override--on" : "override"} style={{ minWidth: 0 }}>
      <div className="cluster" style={{ marginBottom: ".5rem" }}>
        <Label htmlFor={`mlf-${f.key}`}>{f.label}</Label>
        <span className="chip" style={tone ? { background: tone.bg, color: tone.fg } : undefined}>{active ? "escrito neste canal" : f.origin_label}</span>
        {aiButton}
        {!disabled ? (
          <Switch checked={active} onChange={(e) => onToggle(e.target.checked)} label="Sobrescrever" className="ml-auto" />
        ) : null}
      </div>
      {active ? (
        f.type === "select" ? (
          <Select id={`mlf-${f.key}`} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}>
            <option value="">Escolher…</option>
            {(f.options ?? []).map((o) => <option key={o} value={o}>{f.key === "ml_listing_type" ? listingLabel(o) : o}</option>)}
          </Select>
        ) : f.key === "ml_model" ? (
          <Input id={`mlf-${f.key}`} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} maxLength={40} />
        ) : (
          <Input id={`mlf-${f.key}`} type="number" inputMode="numeric" min={1} step={1} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
        )
      ) : (
        <p style={{ margin: 0 }}>{shown || <span className="field__hint">Sem valor{f.type === "number" ? ": preencha o padrão do canal ou a categoria" : ""}</span>}</p>
      )}
    </div>
  );
}
